/**
 * Discover Stream Utilities
 *
 * Parses streaming Oracle text that may contain inline refinement tags
 * (`<Gaia type="refine" text="..." />`) into renderable segments.
 *
 * Reuses GAIA_TAG_REGEX and parseAttributes from gaiaTagParser; output model
 * is simpler — no document map lookups, just text vs. refine chips.
 */

import { GAIA_TAG_REGEX, parseAttributes } from '../../../components/OracleCommons/gaiaTagParser'

// ── Segment types ────────────────────────────────────────────────────

export interface TextSegment {
  type: 'text'
  value: string
}

export interface RefineSegment {
  type: 'refine'
  value: string
}

export type StreamSegment = TextSegment | RefineSegment

/**
 * Parses streaming Oracle text into an array of segments.
 *
 * Plain text becomes `TextSegment`, and `<Gaia type="refine" text="..." />`
 * tags become `RefineSegment` with the tag's `text` attribute as the value.
 *
 * Non-refine Gaia tags are treated as plain text (their `text` attribute
 * is inlined, or the raw match is dropped if no text attribute exists).
 *
 * @param text - Raw streaming text potentially containing <Gaia /> tags
 * @returns Array of StreamSegment for React rendering
 */
export function parseRefinementTags (text: string): StreamSegment[] {
  if (!text) return []

  const segments: StreamSegment[] = []
  let lastIndex = 0

  // Reset regex state (global flag means exec is stateful)
  GAIA_TAG_REGEX.lastIndex = 0

  let match = GAIA_TAG_REGEX.exec(text)
  while (match) {
    const [fullMatch, attrString] = match
    const matchIndex = match.index

    // Add any text before this tag
    if (matchIndex > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, matchIndex) })
    }

    const attrs = parseAttributes(attrString)

    if (attrs.type === 'refine' && attrs.text) {
      segments.push({ type: 'refine', value: attrs.text })
    } else if (attrs.text) {
      // Non-refine tag with text — inline the text
      segments.push({ type: 'text', value: attrs.text })
    }
    // Tags with no text attribute are silently dropped

    lastIndex = matchIndex + fullMatch.length
    match = GAIA_TAG_REGEX.exec(text)
  }

  // Add any remaining text after the last tag
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) })
  }

  return segments
}
